import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Motion";

const services = [
  { key: "kitchens", n: "01", title: "Kitchen Remodels", text: "Cabinetry, counters, islands and layouts that make the heart of the home work harder.", img: "/img/kitchen1.jpg", span: "sm:col-span-2 sm:row-span-2" },
  { key: "bathrooms", n: "02", title: "Bathrooms", text: "Showers, tile, vanities — calm, clean and built to stay dry.", img: "/img/bath2.jpg", span: "" },
  { key: "decks", n: "03", title: "Decks & Patios", text: "Outdoor living built square, sealed and solid underfoot.", img: "/img/deck1.jpg", span: "sm:row-span-2" },
  { key: "flooring", n: "04", title: "Flooring & Tile", text: "Hardwood, LVP and tile, laid level and tight.", img: "/img/floor.jpg", span: "" },
  { key: "painting", n: "05", title: "Painting", text: "Interior and exterior, crisp lines and proper prep.", img: "/img/paint.jpg", span: "sm:col-span-2" },
  { key: "repairs", n: "06", title: "Repairs & Carpentry", text: "Drywall, trim, doors and the punch list you've been putting off.", img: "/img/craft2.jpg", span: "sm:col-span-2" },
];

const Card = ({ s, i }) => {
  const [pos, setPos] = useState({ x: 50, y: 50 });
  return (
    <motion.a
      href="#quote"
      data-testid={`service-card-${s.key}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        setPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
      }}
      className={`group relative overflow-hidden rounded-[20px] bg-[#1A1714] ${s.span}`}
    >
      <img src={s.img} alt={s.title} loading="lazy"
        className="absolute inset-0 h-full w-full object-cover opacity-80 transition-transform duration-[1.4s] ease-[cubic-bezier(.22,1,.36,1)] group-hover:scale-[1.06]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714] via-[#1A1714]/45 to-[#1A1714]/5" />
      <div className="absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{ background: `radial-gradient(380px circle at ${pos.x}% ${pos.y}%, rgba(212,154,70,.28), transparent 60%)` }} />
      <div className="relative flex h-full flex-col justify-between p-6 sm:p-7">
        <div className="flex items-start justify-between">
          <span className="font-mono-d text-xs tracking-[0.2em] text-[#E8DCCB]">{s.n}</span>
          <span className="grid h-10 w-10 place-items-center rounded-full border border-white/30 text-white transition-[background-color,transform] duration-500 group-hover:rotate-45 group-hover:bg-[#C85A32] group-hover:border-[#C85A32]">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
        <div>
          <h3 className="font-serif-d text-3xl sm:text-4xl font-semibold text-[#FAF9F5] leading-none">{s.title}</h3>
          <p className="mt-3 max-w-sm text-sm text-[#E8E2D8] leading-relaxed">{s.text}</p>
        </div>
      </div>
    </motion.a>
  );
};

export const Services = () => (
  <section id="services" className="relative py-24 lg:py-36" data-testid="services-section">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
      <div className="grid gap-8 lg:grid-cols-12 lg:items-end mb-14 lg:mb-20">
        <Reveal className="lg:col-span-7">
          <Eyebrow>What we build</Eyebrow>
          <h2 className="mt-5 font-serif-d text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.95]">
            One crew. <span className="italic text-[#B04E2A]">Every room.</span>
          </h2>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.1}>
          <p className="text-base md:text-lg text-[#605A52] leading-relaxed">
            From full remodels to the small fixes that make a house feel right again — one family team, start to finish, no
            hand-offs.
          </p>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[320px] sm:auto-rows-[280px] grid-flow-dense gap-4 lg:gap-5">
        {services.map((s, i) => <Card key={s.key} s={s} i={i} />)}
      </div>
    </div>
  </section>
);
